/**
 * Longest derived title. Matches the `TITLE_MAX_LENGTH` the removed Title field
 * enforced, so nothing downstream (the portal list, the DevStride work item)
 * ever sees a longer title than it did when a human typed one.
 */
const MAX_TITLE_LENGTH = 120;

/**
 * Builds a request title from the description, now that the modal no longer
 * asks for one.
 *
 * The first line is the summary a person would have typed anyway — people open
 * with the headline and then explain. Falling back to the whole (collapsed)
 * description covers a description that opens with blank lines, where the
 * literal first line is empty and would otherwise produce an empty title that
 * the shared schema's `min(1)` would reject at the API.
 *
 * Pure and total: a blank description returns `''` rather than throwing. Since
 * I24310 a report with markers may be sent with no description, so a blank is
 * reachable from the modal, which calls `deriveReportTitle` below.
 */
export function deriveTitle(description: string): string {
  const collapse = (value: string) => value.replace(/\s+/g, ' ').trim();

  const firstLine = collapse(description.split('\n')[0] ?? '');
  const source = firstLine || collapse(description);

  if (source.length <= MAX_TITLE_LENGTH) return source;
  // -1 leaves room for the ellipsis, so the result is never longer than the max.
  return `${source.slice(0, MAX_TITLE_LENGTH - 1).trimEnd()}…`;
}

/**
 * The title the modal sends: from the description when there is one, else from
 * the first marker note the reporter typed (I24310).
 *
 * A report with markers may be sent with no description, and the note on the
 * first marker is then the nearest thing to a headline the reporter wrote. It
 * becomes the DevStride work item's name, where "Untitled report" tells a
 * triager nothing. With no note either, this returns `''` and the API
 * substitutes `UNTITLED_REQUEST_TITLE`, so there is still one shared fallback.
 */
export function deriveReportTitle(description: string, pins: readonly { text: string }[] = []): string {
  const fromDescription = deriveTitle(description);
  if (fromDescription) return fromDescription;
  for (const pin of pins) {
    const fromNote = deriveTitle(pin.text);
    if (fromNote) return fromNote;
  }
  return '';
}
